/**
 * Invoicing (standalone tax-invoicing) — admin API wire types.
 * Source: apps/admin/src/modules/invoicing (BE branch DEV-250).
 *
 * NOTE: invoicing is the ONE admin surface that serialises camelCase
 * (raw entities, no toSnake). Every other admin module is still
 * snake_case — do not copy snake_case field names here. Money amounts
 * come back as numeric strings ("1322.75"); `id` is an internal bigint
 * string — always route/link by `publicId`.
 */

import { Gender } from 'types/api/users';

export enum InvoiceStatus {
  Draft = 'draft',
  Issued = 'issued',
  Paid = 'paid',
  Cancelled = 'cancelled'
}

export enum InvoicePaymentMethod {
  Stripe = 'stripe',
  BankTransfer = 'bank_transfer'
}

export enum InvoiceLanguage {
  En = 'en',
  De = 'de'
}

export enum InvoiceTaxMode {
  None = 'none',
  PerLine = 'per_line',
  BaseOverride = 'base_override'
}

export enum InvoiceServiceType {
  Consultation = 'consultation',
  SecondOpinion = 'second_opinion',
  Translation = 'translation',
  Teleradiology = 'teleradiology',
  Radiology = 'radiology',
  Other = 'other'
}

// ── Entities (GET responses) ─────────────────────────────────────────

/** A list holds both kinds. A credit note carries a NEGATIVE total. */
export enum InvoiceDocumentType {
  Invoice = 'invoice',
  CreditNote = 'credit_note'
}

export interface Invoice {
  id: string; // internal bigint-as-string — ignore; use publicId
  publicId: string;
  invoiceNumber: string | null; // null while draft
  status: InvoiceStatus;
  language: InvoiceLanguage;
  paymentMethod: InvoicePaymentMethod;
  patientId: number | null;
  patientName: string;
  patientEmail: string | null;
  customerHash: string | null;
  billingAddressId: number | null;
  billingName: string | null;
  billingCompany: string | null;
  billingVatId: string | null;
  billingLine1: string | null;
  billingLine2: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingPostalCode: string | null;
  billingCountry: string | null;
  currency: string;
  subtotalAmount: string;
  taxMode: InvoiceTaxMode;
  taxBaseAmount: string | null;
  taxBaseRate: string | null;
  taxAmount: string;
  totalAmount: string;
  issuerKey: string;
  dueDate: string | null;
  documentType: InvoiceDocumentType;
  // Credit note: the invoice it credits. Null on invoices.
  creditsInvoiceId: string | null;
  // The Stripe-hosted pay page. THIS is the pay link for invoices issued on
  // the hosted flow — never cache it, Stripe expires it.
  stripeHostedInvoiceUrl: string | null;
  stripeInvoiceId: string | null;
  stripePaymentLinkId: string | null;
  /** @deprecated no longer populated — payment moved on-domain; build the pay
   *  URL from publicId ({SITE_URL}/{lang}/pay/{publicId}) instead. */
  stripePaymentLinkUrl: string | null;
  stripeCheckoutSessionId: string | null;
  stripePaymentIntentId: string | null;
  receiptUrl: string | null;
  pdfUrl: string | null; // S3 key — use the /pdf route, not this directly
  issuedAt: string | null;
  paidAt: string | null;
  paidBy: number | null;
  createdBy: number;
  appointmentId: number | null;
  createdAt: string;
  updatedAt: string;
}

/** Body of `POST /invoicing/:ref/credit-notes`. */
export interface CreditNoteInput {
  // POSITIVE, in major units. The stored document is negative.
  amount: number;
  // A real choice, not a default: true sends the money back through Stripe
  // (paid Stripe invoices only), false writes the document alone.
  refundViaStripe: boolean;
  reason?: string;
}

export interface InvoiceLine {
  id: string;
  publicId: string;
  invoiceId: string;
  sortIndex: number;
  lineType: InvoiceServiceType;
  productId: string | null;
  doctorId: number | null;
  doctorName: string | null;
  descriptionEn: string | null;
  descriptionDe: string | null;
  quantity: string;
  unitAmount: string;
  taxRate: string;
  // Per-line discount applied by the BE: percent = % of gross, amount = fixed.
  // null/0 when the line has no discount. lineNet is already net of it.
  discountType: InvoiceDiscountType | null;
  discountValue: string | null;
  lineTax: string;
  lineNet: string;
  lineTotal: string;
  createdAt: string;
}

export interface InvoicingProduct {
  id: string;
  publicId: string;
  nameEn: string;
  nameDe: string;
  serviceType: InvoiceServiceType;
  defaultCost: string;
  currency: string;
  defaultTaxRate: string;
  active: boolean;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface BillingAddress {
  id: string;
  publicId: string;
  patientId: number;
  fullName: string;
  company: string | null;
  vatId: string | null;
  line1: string;
  line2: string | null;
  city: string;
  state: string | null;
  postalCode: string | null;
  country: string; // ISO-2
  isDefault: boolean;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
}

// ── Request bodies (already camelCase on the BE) ─────────────────────

export type InvoiceDiscountType = 'amount' | 'percent';

export interface InvoiceLineInput {
  lineType?: InvoiceServiceType;
  productId?: string | null;
  doctorId?: number | null;
  doctorName?: string | null;
  descriptionEn?: string | null;
  descriptionDe?: string | null;
  quantity: number;
  unitAmount: number;
  taxRate?: number; // per-line %, used when taxMode = per_line
  // Per-line discount (BE-computed): amount = fixed off net, percent = % of
  // gross (0–100). Over-values are clamped; tax applies to the discounted net.
  discountType?: InvoiceDiscountType;
  discountValue?: number;
}

/**
 * Inline billing-address snapshot for a non-user customer (they have no saved
 * address book, so the address travels with the invoice). Country is ISO-2.
 */
export interface InvoiceBillingAddressSnapshot {
  name: string;
  company?: string | null;
  vatId?: string | null;
  line1: string;
  line2?: string | null;
  city: string;
  state?: string | null;
  postalCode?: string | null;
  country: string; // ISO-2
}

export interface CreateInvoiceInput {
  patientId?: number | null;
  // Bill a non-user customer instead of a patient (exactly one of the two; when
  // customerId is set the BE ignores patientId). Accepts id or public_id.
  customerId?: string | null;
  billingAddress?: InvoiceBillingAddressSnapshot | null;
  patientName: string;
  patientEmail?: string | null;
  billingAddressId?: number | null;
  language?: InvoiceLanguage;
  currency?: string;
  paymentMethod?: InvoicePaymentMethod;
  taxMode?: InvoiceTaxMode;
  taxBaseAmount?: number | null; // only when taxMode = base_override
  taxBaseRate?: number | null; // only when taxMode = base_override
  dueDate?: string | null; // YYYY-MM-DD
  appointmentId?: number | null;
  issuerKey?: string;
  lines: InvoiceLineInput[];
  issue?: boolean; // default true → assigns number + PDF + (stripe) link
}

export interface Customer {
  id: string;
  publicId: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  // Required when creating, never on update — a customer created before
  // these columns existed stays editable, so both can still be null here.
  dateOfBirth: string | null; // ISO date
  gender: Gender | null;
  // The customer's own billing address (the BE snapshots it onto the invoice).
  company: string | null;
  vatId: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null; // ISO-2
}

/** Which table a party row came from — decides the field the invoice carries. */
export enum PartyKind {
  Patient = 'patient',
  Customer = 'customer'
}

/**
 * One row of `GET /invoicing/parties` — a combined search over platform
 * patients and billing-only customers. An invoice can be raised against
 * either, and searching only patients hid the customers, so the same person
 * was created twice and their invoices never reconciled.
 */
export interface PartyResult {
  kind: PartyKind;
  // Patient: the numeric core.patients.id. Customer: the public_id UUID.
  ref: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  dateOfBirth: string | null; // ISO date
  gender: Gender | null;
  // Set when a party of the OTHER kind shares this email — this person
  // probably already exists twice. Surfaced so an admin picks the existing
  // record instead of adding a third.
  duplicateOf?: PartyKind;
}

export interface CustomerInput {
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  // CustomerDto requires both on create. UpdateCustomerDto requires
  // neither, which Partial<CustomerInput> below already expresses.
  dateOfBirth: string; // ISO date, YYYY-MM-DD
  gender: Gender;
  company?: string | null;
  vatId?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null; // ISO-2
}

export type UpdateCustomerInput = Partial<CustomerInput>;

export interface CountryOption {
  code: string;
  name: string;
}

// Invoice-number counter (super_admin only). <PREFIX>-<NNNN>; the counter is
// per prefix. lastValue = last issued; nextValue = what the next invoice gets.
export interface NumberCounter {
  scope: string;
  lastValue: number;
  nextValue: number;
}

export type InvoiceSortBy =
  | 'id'
  | 'invoiceNumber'
  | 'createdAt'
  | 'dueDate'
  | 'totalAmount'
  | 'status';

export interface InvoiceListFilters {
  // Scope to one platform patient (core.patients.id) — powers the patient
  // invoice history tab. Invoices billed to a non-user customer carry no
  // patientId and are excluded by design.
  patientId?: number;
  status?: InvoiceStatus;
  paymentMethod?: InvoicePaymentMethod;
  search?: string; // patient name OR invoice number
  from?: string; // ISO datetime on createdAt
  to?: string;
  sortBy?: InvoiceSortBy;
  sortOrder?: 'ASC' | 'DESC';
  page?: number;
  limit?: number;
}

export interface MarkPaidInput {
  paidAt?: string | null; // ISO datetime
  receiptUrl?: string | null;
}

export interface ProductInput {
  nameEn: string;
  nameDe: string;
  serviceType: InvoiceServiceType;
  defaultCost: number;
  currency?: string;
  defaultTaxRate?: number;
  active?: boolean;
}

export type UpdateProductInput = Partial<ProductInput>;

export interface BillingAddressInput {
  patientId: number;
  fullName: string;
  company?: string | null;
  vatId?: string | null;
  line1: string;
  line2?: string | null;
  city: string;
  state?: string | null;
  postalCode?: string | null;
  country: string; // ISO-2
  isDefault?: boolean;
}

export type UpdateBillingAddressInput = Partial<
  Omit<BillingAddressInput, 'patientId'>
>;

// ── Composite responses ──────────────────────────────────────────────

export interface InvoiceListResponse {
  data: Invoice[];
  total: number;
  page: number;
  limit: number;
}

export interface InvoiceDetail {
  invoice: Invoice;
  lines: InvoiceLine[];
}

export interface CreateInvoiceResponse {
  invoice: Invoice;
  // On-domain pay page URL ({SITE_URL}/{lang}/pay/{publicId}); present when
  // issued AND stripe. Renamed from paymentLinkUrl when payment moved
  // on-domain.
  payUrl: string | null;
}
